import { useRoute } from 'vue-router'
const route = useRoute()

// ...existing code...

// Place this watcher after selectedCategory is defined
watch(
() => route.query.category,
(newCategory) => {
if (newCategory && newCategory !== selectedCategory.value) {
selectedCategory.value = newCategory
} else if (!newCategory) {
selectedCategory.value = 'all'
}
}
)
<template>
    <div ref="componentRef" class="min-h-screen bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">

            <div class="text-center mb-0.2">
                <h1 class="font-header text-[3rem] xl:text-[3rem] font-bold text-primary mb-0.2">Books</h1>
                <div class="font-body text-[1.125rem] text-gray-700 max-w-4xl mx-auto leading-[1.75] content-page-description"
                    v-html="pageDescription || 'Explore our catalog of books — from new releases to coming-soon titles.'">
                </div>
            </div>



            <div v-if="activeFilter" class="mb-1.5 flex items-center gap-1.5">
                <span
                    class="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-md text-sm font-sans">
                    <span class="text-gray-600">Category:</span>
                    <span class="font-semibold text-accent">{{ activeFilter }}</span>
                    <button @click="clearFilter" class="ml-2 text-gray-400 hover:text-primary transition-colors"
                        aria-label="Clear filter">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </span>
            </div>

            <div class="flex flex-col xl:flex-row gap-1.5">
                <main class="flex-1">
                    <div v-if="isSearching && searchQuery.trim()">
                        <section class="mb-2.5">
                            <div class="flex items-center gap-1.5 mb-1.5 overflow-hidden min-w-0">
                                <h2
                                    class="font-header text-[1.5rem] xl:text-[1.5rem] font-bold text-primary flex-1 min-w-0 truncate">
                                    Results for "{{ searchQuery }}"
                                </h2>
                                <button @click="clearSearch"
                                    class="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-sans text-primary hover:bg-creme-100 transition-colors flex-shrink-0">
                                    Clear Results
                                </button>
                            </div>
                            <div v-if="loading" class="text-center py-1">
                                <Loader size="large" />
                            </div>
                            <div v-else-if="searchResults.length > 0" class="flex flex-wrap gap-1.5 justify-start">
                                <BookCard v-for="book in searchResults" :key="book.id" :book="book" />
                            </div>
                            <div v-else class="text-center py-1">
                                <p class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">{{ searchEmptyMessage
                                    }}</p>
                            </div>
                        </section>
                    </div>

                    <div v-else>
                        <div v-if="loading && !isSearching" class="text-center py-1">
                            <Loader size="large" />
                        </div>

                        <template v-else>
                            <div v-if="selectedCategory === 'all'">
                                <section class="mb-2.5">
                                    <h2
                                        class="font-header text-[1.5rem] xl:text-[1.5rem] font-bold text-primary mb-1.5">
                                        All Books
                                    </h2>
                                    <div v-if="featuredBooks.length > 0" class="flex flex-wrap gap-1.5 justify-start">
                                        <BookCard v-for="book in featuredBooks" :key="book.id" :book="book" />
                                    </div>
                                    <div v-else class="text-center py-1">
                                        <p class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">{{
                                            allBooksEmptyMessage }}</p>
                                    </div>
                                </section>
                            </div>

                            <div v-else>
                                <section class="mb-2.5">
                                    <h2
                                        class="font-header text-[1.5rem] xl:text-[1.5rem] font-bold text-primary mb-1.5">
                                        {{ activeFilter || 'Books' }}
                                    </h2>
                                    <div v-if="selectedCategory !== 'all' && selectedCategory !== 'coming-soon'">
                                        <div v-if="categories && categories.length">
                                            <div v-for="cat in categories" :key="cat.id">
                                                <template v-if="cat.id === selectedCategory && cat.description">
                                                    <div
                                                        class="font-body text-[1.125rem] text-gray-700 text-left mb-2 max-w-4xl leading-[1.75]">
                                                        {{ cat.description }}
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                    <template v-if="featuredBooks.length > 0">
                                        <div class="flex flex-wrap gap-1.5 justify-start">
                                            <BookCard v-for="book in featuredBooks" :key="book.id" :book="book" />
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="text-center py-1">
                                            <p class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">{{
                                                categoryEmptyMessage }}</p>
                                        </div>
                                    </template>
                                </section>
                            </div>
                        </template>
                    </div>
                </main>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import BookCard from './BookCard.vue'

const componentRef = ref(null)

import Loader from './Loader.vue'
import { useLoadingState } from '../composables/useLoadingState'
import { useDataTranslation } from '../composables/useDataTranslation'
import { useEmptyMessages } from '../composables/useEmptyMessages'
import { publicService } from '../services/public'
import {
    getAllBooks,
    searchBooks,
    getBooksByCategory,
    getComingSoon
} from '../utils/books'

const { getEmptyMessage } = useEmptyMessages()

const searchQuery = ref('')
const selectedCategory = ref('all')
const activeFilter = ref('')
const isSearching = ref(false)
const { loading, setLoading } = useLoadingState(false)
const pageDescription = ref('')

const featuredBooks = ref([])
const searchResults = ref([])

const searchEmptyMessage = computed(() => {
    return getEmptyMessage('search', searchQuery.value) || `No books found for "${searchQuery.value}".`
})

const allBooksEmptyMessage = computed(() => {
    return getEmptyMessage('all') || 'No books found.'
})

const categoryEmptyMessage = computed(() => {
    const categoryObj = categories.value.find(c => c.id === selectedCategory.value)
    if (categoryObj) {
        const categoryKey = selectedCategory.value
        return getEmptyMessage(categoryKey) || `No books found for Category ${categoryObj.name}.`
    }
    return getEmptyMessage('all') || 'No books found.'
})

const { handleLanguageChange: handleTranslation, translateIfReady } = useDataTranslation(componentRef, loading, '.min-h-screen.bg-white', setLoading)

const handleLanguageChange = async () => {
    await loadBooks()
    if (searchQuery.value.trim()) {
        await performSearch()
    }
    await loadPageDescription()
    await handleTranslation()
}

const getQueryFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('q') || ''
}

const getCategoryFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('category') || 'all'
}

const initializeSearch = () => {
    const query = getQueryFromURL()
    if (query) {
        searchQuery.value = query
        isSearching.value = true
    } else {
        searchQuery.value = ''
        isSearching.value = false
    }
}

const initializeCategory = () => {
    const category = getCategoryFromURL()
    if (category && category !== 'all') {
        selectedCategory.value = category
        const categoryObj = categories.value.find(c => c.id === category)
        activeFilter.value = categoryObj ? categoryObj.name : ''
    } else {
        selectedCategory.value = 'all'
        activeFilter.value = ''
    }
}

watch(selectedCategory, (newValue) => {
    const categoryObj = categories.value.find(c => c.id === newValue)
    activeFilter.value = categoryObj && newValue !== 'all' ? categoryObj.name : ''
})

const handleURLChange = () => {
    initializeSearch()
    initializeCategory()
}

const categories = ref([
    { id: 'all', name: 'All Books', slug: null },
    { id: 'coming-soon', name: 'Coming Soon', slug: null }
])

const loadCategories = async () => {
    try {
        const response = await publicService.getCategories({ limit: 1000 })
        const apiCategories = response.categories || []

        // Map API categories to the format we need, using slug as the id
        const dynamicCategories = apiCategories.map(cat => ({
            id: cat.slug,
            name: cat.name,
            slug: cat.slug,
            description: cat.description || ''
        }))

        // Combine static categories (All Books, Coming Soon) with dynamic ones
        categories.value = [
            { id: 'all', name: 'All Books', slug: null },
            { id: 'coming-soon', name: 'Coming Soon', slug: null },
            ...dynamicCategories
        ]
    } catch (error) {
        console.error('Failed to load categories:', error)
        // Keep default categories on error
    }
}

const loadPageDescription = async () => {
    try {
        const page = await publicService.getPage('Books')
        if (page && page.description) {
            pageDescription.value = page.description
        }
    } catch (error) {
        console.error('Failed to load page description:', error)
    }
}

const clearFilter = () => {
    selectedCategory.value = 'all'
    activeFilter.value = ''
    window.history.pushState({}, '', '/books')
    window.dispatchEvent(new CustomEvent('app:url-change', {
        detail: {
            url: window.location.href,
            category: 'all'
        }
    }))
}

const clearSearch = async () => {
    searchQuery.value = ''
    searchResults.value = []
    isSearching.value = false
    window.history.pushState({}, '', '/books')
    window.dispatchEvent(new CustomEvent('app:url-change', {
        detail: {
            url: window.location.href,
            category: selectedCategory.value
        }
    }))
    await loadBooks()
}

const loadBooks = async () => {
    setLoading(true)
    try {
        if (selectedCategory.value === 'all') {
            featuredBooks.value = await getAllBooks()
        } else if (selectedCategory.value === 'coming-soon') {
            featuredBooks.value = await getComingSoon()
        } else {
            // For dynamic categories, use the category slug directly
            featuredBooks.value = await getBooksByCategory(selectedCategory.value)
        }
    } catch (error) {
        console.error('Failed to load books:', error)
    } finally {
        setLoading(false)
    }
}

const performSearch = async () => {
    if (!searchQuery.value.trim()) {
        searchResults.value = []
        return
    }

    const trimmedQuery = searchQuery.value.trim()

    setLoading(true)
    try {
        searchResults.value = await searchBooks(trimmedQuery)
    } catch (error) {
        console.error('Failed to search books:', error)
        searchResults.value = []
    } finally {
        setLoading(false)
    }
}

watch(searchQuery, async (newValue) => {
    if (newValue.trim()) {
        isSearching.value = true
        await performSearch()
    } else {
        isSearching.value = false
        searchResults.value = []
    }
})

watch(selectedCategory, async () => {
    await loadBooks()
})


onMounted(async () => {
    window.addEventListener('language-changed', handleLanguageChange)
    window.addEventListener('popstate', handleURLChange)
    window.addEventListener('app:url-change', handleURLChange)

    // Load categories first
    await loadCategories()

    initializeSearch()
    initializeCategory()

    if (searchQuery.value.trim()) {
        await performSearch()
    } else {
        await loadBooks()
    }

    await loadPageDescription()
    await translateIfReady()
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
    window.removeEventListener('popstate', handleURLChange)
    window.removeEventListener('app:url-change', handleURLChange)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}

/* Chevron arrow styling */
.chevron {
    display: inline-block;
    font-size: 2rem;
    font-weight: bold;
    margin-left: 2rem;
    transition: transform 0.2s;
    line-height: 1;
    color: #fff;
}

.chevron-rotated {
    transform: rotate(90deg);
}
</style>
